import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'
import { spanDays } from '@krp/ui-kit'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { formatDate, formatDateTime, formatMoney } from '../../pricing/model/format'
import { cashReceiptsViewOf, periodError, type CashRegistersResult } from './selectors'
import { nodeExists, registerKeysOf } from './tree'
import type {
  CashCorrectionReceipt,
  CashCorrectionReceipts,
  CashCorrectionRowVm,
  CashCorrectionsArgs,
  CashCorrectionsVm,
  CashReceiptsIntentState,
  CashReceiptsViewState,
} from './types'

/** Самый длинный период чеков коррекции: служба читает их за период не длиннее 31 дня, как список чеков. */
export const CASH_CORRECTIONS_MAX_DAYS = 31

export type CashCorrectionsResult = QueryResultLike<OneCEnvelope<CashCorrectionReceipts>>

const IDLE: QueryView = { status: 'idle', refreshing: false, error: null }

/** Почему чеки коррекции за этот период не спрашиваются; `null` — можно. */
export function correctionsPeriodError(from: string, to: string): string | null {
  const common = periodError(from, to)
  if (common !== null) return common
  return spanDays(from, to) > CASH_CORRECTIONS_MAX_DAYS
    ? `Чеки коррекции показываются за период не больше ${CASH_CORRECTIONS_MAX_DAYS} дня — выберите день или месяц`
    : null
}

/** Аргументы чеков коррекции или `null`: нет базы или период не подходит. Читается вся база — место дерева отбирает экран. */
export function cashCorrectionsArgsOf(resourceId: string, view: CashReceiptsViewState, today: string): CashCorrectionsArgs | null {
  const from = view.from ?? today
  const to = view.to ?? today
  if (resourceId === '' || correctionsPeriodError(from, to) !== null) return null
  return { resourceId, from, to, ...(view.refreshStamp > 0 ? { fresh: view.refreshStamp } : {}) }
}

const yesNo = (value: boolean | null): string => (value === null ? 'не известно' : value ? 'да' : 'нет')

function correctionRowOf(item: CashCorrectionReceipt): CashCorrectionRowVm {
  const settlement = item.settlementLabel ?? 'тип расчёта не указан'
  const other = item.otherAmount !== null && item.otherAmount !== 0
    ? [formatMoney(item.otherAmount), item.otherPaymentTypes.join(', ')].filter((part) => part !== '').join(' · ')
    : ''
  return {
    id: item.key,
    refund: item.settlement === 'income-refund',
    kindLabel: `Коррекция · ${settlement}`,
    number: item.receiptNumber ?? item.number ?? '',
    at: formatDateTime(item.date),
    register: item.registerName ?? (item.registerKey === null ? 'касса не указана' : 'касса не найдена в справочнике'),
    cashier: item.cashierName ?? '',
    total: item.amount,
    cash: item.cashAmount,
    card: item.electronicAmount,
    muted: !item.posted,
    settlement: item.storno === true ? `${settlement} · сторно` : settlement,
    corrected: formatDate(item.correctedDate),
    correctionKind: [item.correctionKindLabel ?? '—', item.description].filter((part) => part !== null).join(' · '),
    order: item.orderNumber ?? '',
    printed: yesNo(item.printed),
    other,
  }
}

export interface CashCorrectionsInput {
  resourceId: string
  today: string
  intent: CashReceiptsIntentState
  registers: CashRegistersResult
  /** `null` — запроса нет (период не подходит или таб не открыт). */
  corrections: CashCorrectionsResult | null
}

/**
 * Таб «Чеки коррекции»: чеки коррекции периода вкладки у выбранного в дереве места, новые сверху. Место отбирает экран — запрос идёт
 * по всей базе, как у чеков. В итоги и графики чеки коррекции не входят (RP-ST15).
 */
export function cashCorrectionsVmOf(input: CashCorrectionsInput): CashCorrectionsVm {
  const view = cashReceiptsViewOf(input.intent, input.resourceId)
  const from = view.from ?? input.today
  const to = view.to ?? input.today
  const error = correctionsPeriodError(from, to)
  const directory = input.registers.data?.data
  const selectedId = nodeExists(directory, view.nodeId) ? view.nodeId : ''
  const placeKeys = selectedId === '' ? null : new Set(registerKeysOf(directory, selectedId))
  const listed = input.corrections?.data?.data
  const all = listed?.items ?? []
  const shown = all.filter((item) => placeKeys === null || (item.registerKey !== null && placeKeys.has(item.registerKey)))
  return {
    query: input.corrections === null ? IDLE : queryView(input.corrections, (value) => value.data.items.length === 0),
    rows: shown.map(correctionRowOf),
    periodError: error,
    truncatedNote: listed?.truncated === true ? 'Чеков коррекции за период больше, чем прочитано: показаны самые новые. Сократите период.' : null,
    filteredOut: all.length > 0 && shown.length === 0,
  }
}
